import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import { connect, useDispatch } from 'react-redux';
import {
    ButtonGroup,
    Button,
    SelectControl,
    SegmentLoader,
    Row,
    Col,
    InputControl,
} from '@si-armes/dsfr-react';
import { useForm, FormProvider } from 'react-hook-form';
import moment from 'moment';
import InputFiles from 'components/Commun/Form/InputFiles';
import { getDateFormatWithSlash } from 'components/Commun/Form/maskFunctions';
import SelectPays from 'components/form/referentiel/select/SelectPays';
import SelectCommune from 'components/form/referentiel/select/SelectCommune';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_COMPTE_CREER_RESPONSABLE_LEGAL_URL,
    API_ENREGISTRER_PREUVE_AUTORITE_PARENTALE_URL,
    API_ENREGISTRER_CERTIFICAT_HONNEUR_URL,
    API_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL_URL,
    CONTEXT_CREER_COMPTE_ETAPE_RESPONSABLE_LEGAL,
    CONTEXT_ENREGISTRER_PREUVE_AUTORITE_PARENTALE,
    CONTEXT_ENREGISTRER_CERTIFICAT_HONNEUR,
    CONTEXT_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL,
} from 'constants/apiUrls.constants';
import {
    REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
    REGEXP_DATE_AVEC_ANNEE,
    REGEXP_NOM_PRENOM,
    AGE_MAJORITE,
} from 'constants/contraintes.constants';
import {
    CLEAR_ALERTES,
    formaterOptions,
    getMessageErreurChampManquant,
    getPatternHookFormValidation,
} from 'utils/utils';
import { MESSAGE_DATE_INVALIDE } from 'constants/messagePersonalise.constants';
import formaterCreationResponsableLegal from './FormaterCreationResponsableLegal';
import InfoChampsObligatoire from '../../../../../../components/form/infoObligatoire/InfoChampsObligatoire';
import { hideAllNotification } from '../../../../../../store/alertes/alertesReducer';

const CreerCompteResponsableLegal = ({
    dataResponsableLegalRenseignee,
    setEtapeResponsableLegal,
    postApi,
    isLoading,
    retourEtape,
    erreur,
    message,
    data,
    uuidBrouillon,
    isLoadingPreuveAutoriteParentale,
    erreurPreuveAutoriteParentale,
    dataRenseignerPreuveAutoriteParentale,
    isLoadingCertificatHonneur,
    erreurCertificatHonneur,
    dataRenseignerCertificatHonneur,
    isLoadingPieceIdentite,
    erreurPieceIdentite,
    dataRenseignerPieceIdentite,
    clearAlertes,
    reinitialiseApi,
}) => {
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: {
            paysNaissance: '75',
            ...dataResponsableLegalRenseignee,
        },
    });

    const dispatch = useDispatch();
    const { register, handleSubmit, getValues, watch, formState, trigger, setValue } = hookForm;
    const [dataResponsableLegal, setDataResponsableLegal] = useState();

    const [valueRecherche, setValueRecherche] = useState(getValues('lieuNaissance') ?? '');

    const choixSexe = [
        { key: 'F', value: '2', libelle: 'Féminin' },
        { key: 'M', value: '1', libelle: 'Masculin' },
    ];

    const typePieceIdentite = [
        { key: 'CNI', value: 2, libelle: "Carte nationale d'identité" },
        { key: 'passeport', value: 33, libelle: 'Passeport' },
        { key: 'titreSejour', value: 34, libelle: 'Titre de séjour' },
        { key: 'pieceIdentiteEtrangere', value: 35, libelle: 'Pièce identité étrangère' },
    ];

    useEffect(() => {
        if (dataResponsableLegalRenseignee) {
            setValue(
                'preuveAutoriteParentale',
                dataResponsableLegalRenseignee.preuveAutoriteParentale,
            );
            setValue('certificatHonneur', dataResponsableLegalRenseignee.certificatHonneur);
        }
    }, []);

    async function postPreuveAutoriteParentale() {
        const fichier = dataResponsableLegal?.preuveAutoriteParentale?.[0];
        await postApi(
            usagerClientReducer,
            API_ENREGISTRER_PREUVE_AUTORITE_PARENTALE_URL,
            {
                preuveAutoriteParentale: await fichier.getFileToSend(),
                uuidBrouillon,
            },
            CONTEXT_ENREGISTRER_PREUVE_AUTORITE_PARENTALE,
        );
    }

    async function postCertificatHonneur() {
        const fichier = dataResponsableLegal?.certificatHonneur?.[0];
        await postApi(
            usagerClientReducer,
            API_ENREGISTRER_CERTIFICAT_HONNEUR_URL,
            {
                certificatHonneur: await fichier.getFileToSend(),
                uuidBrouillon,
            },
            CONTEXT_ENREGISTRER_CERTIFICAT_HONNEUR,
        );
    }

    async function postPieceIdentite() {
        const fichier = dataResponsableLegal?.piecesIdentite?.[0];
        await postApi(
            usagerClientReducer,
            API_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL_URL,
            {
                pieceIdentite: await fichier.getFileToSend(),
                uuidBrouillon,
            },
            CONTEXT_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL,
        );
    }

    function conditionPourPasserEtapeSuivante() {
        const informationsResponsableLegalEnvoyee = isLoading === false && data && !erreur;

        const preuveAutoriteParentaleEnvoyee =
            !isLoadingPreuveAutoriteParentale &&
            dataRenseignerPreuveAutoriteParentale &&
            !erreurPreuveAutoriteParentale;

        const certificatHonneurEnvoyee =
            !isLoadingCertificatHonneur &&
            dataRenseignerCertificatHonneur &&
            !erreurCertificatHonneur;

        const pieceIdentiteEnvoyee =
            !isLoadingPieceIdentite && dataRenseignerPieceIdentite && !erreurPieceIdentite;

        return (
            informationsResponsableLegalEnvoyee &&
            preuveAutoriteParentaleEnvoyee &&
            certificatHonneurEnvoyee &&
            pieceIdentiteEnvoyee
        );
    }

    useEffect(() => {
        if (conditionPourPasserEtapeSuivante()) {
            setEtapeResponsableLegal(dataResponsableLegal);
        }
    }, [
        isLoading,
        data,
        erreur,
        isLoadingPreuveAutoriteParentale,
        dataRenseignerPreuveAutoriteParentale,
        erreurPreuveAutoriteParentale,
        isLoadingCertificatHonneur,
        dataRenseignerCertificatHonneur,
        erreurCertificatHonneur,
        isLoadingPieceIdentite,
        dataRenseignerPieceIdentite,
        erreurPieceIdentite,
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
        reinitialiseApi(CONTEXT_ENREGISTRER_PREUVE_AUTORITE_PARENTALE);
        reinitialiseApi(CONTEXT_ENREGISTRER_CERTIFICAT_HONNEUR);
        reinitialiseApi(CONTEXT_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL);
        reinitialiseApi(CONTEXT_CREER_COMPTE_ETAPE_RESPONSABLE_LEGAL);
        return () => {
            reinitialiseApi(CONTEXT_ENREGISTRER_PREUVE_AUTORITE_PARENTALE);
            reinitialiseApi(CONTEXT_ENREGISTRER_CERTIFICAT_HONNEUR);
            reinitialiseApi(CONTEXT_ENREGISTRER_PIECE_IDENTITE_RESPONSABLE_LEGAL);
            reinitialiseApi(CONTEXT_CREER_COMPTE_ETAPE_RESPONSABLE_LEGAL);
        };
    }, []);

    useEffect(() => {
        trigger(['prenomDeux', 'prenomTrois', 'prenomQuatre', 'prenomUsuel']);
    }, [
        watch('prenomUn'),
        watch('prenomDeux'),
        watch('prenomTrois'),
        watch('prenomQuatre'),
        watch('prenomUsuel'),
    ]);

    useEffect(() => {
        trigger(['paysNaissance']);
    }, [watch('paysNaissance')]);

    const validatePrenomUsuel = (valuePrenomUsuel) => {
        if (
            valuePrenomUsuel &&
            valuePrenomUsuel !== getValues('prenomUn') &&
            valuePrenomUsuel !== getValues('prenomDeux') &&
            valuePrenomUsuel !== getValues('prenomTrois') &&
            valuePrenomUsuel !== getValues('prenomQuatre')
        ) {
            return 'Le prénom usuel doit correspondre à un des autres prénoms';
        }
        return null;
    };

    const validateDate = (dateValue) => {
        const dateSplit = dateValue.split('/');
        const dateExistante = new Date(dateSplit[2], dateSplit[1] - 1, dateSplit[0]);
        if (
            (dateSplit &&
                moment(`${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}`).diff(moment()) > 0) ||
            (dateSplit[1] !== '00' &&
                dateSplit[0] !== '00' &&
                (dateExistante.getFullYear() !== parseInt(dateSplit[2], 10) ||
                    dateExistante.getMonth() + 1 !== parseInt(dateSplit[1], 10) ||
                    dateExistante.getDate() !== parseInt(dateSplit[0], 10)))
        ) {
            return MESSAGE_DATE_INVALIDE;
        }
        return null;
    };

    const validateAge = (valueDateNaissance) => {
        if (valueDateNaissance.length === 10) {
            const dateNaissanceSplit = valueDateNaissance.split('/');
            const thisYear = dateNaissanceSplit[2];
            const birthDay = dateNaissanceSplit[0];
            const birthMonth = dateNaissanceSplit[1];
            const valideAge = new RegExp(REGEXP_DATE_AVEC_ANNEE);

            if (valideAge.test(thisYear)) {
                const age = moment().diff(
                    moment(thisYear + birthMonth + birthDay, 'YYYYMMDD'),
                    'years',
                );

                if (age >= AGE_MAJORITE) {
                    return null;
                }

                return "La date de naissance renseignée correspond à une personne mineure. Seule une personne majeure peut être responsable légal d'un détenteur mineur";
            }
        }

        return null;
    };

    const hasFormValid = () => {
        let formulaireEstValide = formState.isValid;

        formulaireEstValide =
            formulaireEstValide &&
            getValues('piecesIdentite') &&
            getValues('piecesIdentite').length > 0;

        formulaireEstValide =
            formulaireEstValide &&
            getValues('preuveAutoriteParentale') &&
            getValues('preuveAutoriteParentale').length > 0;

        formulaireEstValide =
            formulaireEstValide &&
            getValues('certificatHonneur') &&
            getValues('certificatHonneur').length > 0;

        return formulaireEstValide;
    };

    useEffect(() => {
        const envoyerRequetes = async () => {
            if (dataResponsableLegal?.preuveAutoriteParentale) {
                await postPreuveAutoriteParentale();
            }

            if (dataResponsableLegal?.certificatHonneur) {
                await postCertificatHonneur();
            }

            if (dataResponsableLegal?.piecesIdentite) {
                await postPieceIdentite();
            }
        };

        if (!isLoading && data && !erreur) {
            envoyerRequetes();
        }
    }, [isLoading, data, erreur, dataResponsableLegal]);

    const onSubmit = async (dataForm) => {
        dispatch({ type: CLEAR_ALERTES });
        window.scrollTo(0, 0);
        setDataResponsableLegal(dataForm);
        const dataFormat = await formaterCreationResponsableLegal(dataForm, uuidBrouillon);
        clearAlertes();
        await postApi(
            usagerClientReducer,
            API_COMPTE_CREER_RESPONSABLE_LEGAL_URL,
            {
                ...dataFormat,
            },
            CONTEXT_CREER_COMPTE_ETAPE_RESPONSABLE_LEGAL,
        );
    };

    const renderMessage = (msg) => {
        return msg.split('\n').map((line) => <p key={line}>{line}</p>);
    };

    return (
        <SegmentLoader
            isLoading={
                isLoading ||
                isLoadingPreuveAutoriteParentale ||
                isLoadingCertificatHonneur ||
                isLoadingPieceIdentite
            }
        >
            {erreur && message && (
                <Row>
                    <Col col={12}>
                        <div className="fr-error">{renderMessage(message)}</div>
                    </Col>
                </Row>
            )}
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                        <Col col={12}>
                            <InfoChampsObligatoire />
                        </Col>
                    </Row>

                    <Row className="margin-top-lg">
                        <Col col={12}>
                            <h3>Identité du responsable légal du détenteur mineur</h3>
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <SelectControl
                                id="sexe"
                                name="Sexe *"
                                label="Sexe *"
                                options={formaterOptions(choixSexe)}
                                required
                                state={formState.errors?.sexe ? 'error' : null}
                                message={formState.errors?.sexe?.message}
                                {...register('sexe', {
                                    required: getMessageErreurChampManquant('sexe'),
                                })}
                                placeholder="-- Choisir une option --"
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="family-name"
                                id="nom"
                                label="Nom *"
                                required
                                uppercase
                                {...register('nom', {
                                    required: getMessageErreurChampManquant('nom'),
                                    pattern: getPatternHookFormValidation(REGEXP_NOM_PRENOM, 'nom'),
                                })}
                                state={formState.errors?.nom ? 'error' : null}
                                message={formState.errors?.nom?.message}
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="family-name"
                                id="nomUsage"
                                label="Nom d'usage"
                                hintText="Nom de famille utilisé dans la vie courante, à renseigner s'il ne s'agit pas du nom de naissance."
                                uppercase
                                {...register('nomUsage', {
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        "nom d'usage",
                                    ),
                                })}
                                state={formState.errors?.nomUsage ? 'error' : null}
                                message={formState.errors?.nomUsage?.message}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="given-name"
                                id="prenomUn"
                                label="Premier prénom *"
                                required
                                uppercase
                                {...register('prenomUn', {
                                    required: getMessageErreurChampManquant('premier prénom'),
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        'premier prénom',
                                    ),
                                })}
                                state={formState.errors?.prenomUn ? 'error' : null}
                                message={formState.errors?.prenomUn?.message}
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="additional-name"
                                id="prenomDeux"
                                label="Deuxième prénom"
                                disabled={!getValues('prenomUn')}
                                {...register('prenomDeux', {
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        'deuxième prénom',
                                    ),
                                })}
                                uppercase
                                state={formState.errors?.prenomDeux ? 'error' : null}
                                message={formState.errors?.prenomDeux?.message}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="additional-name"
                                id="prenomTrois"
                                label="Troisième prénom"
                                disabled={!getValues('prenomUn') || !getValues('prenomDeux')}
                                {...register('prenomTrois', {
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        'troisième prénom',
                                    ),
                                })}
                                uppercase
                                state={formState.errors?.prenomTrois ? 'error' : null}
                                message={formState.errors?.prenomTrois?.message}
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="additional-name"
                                id="prenomQuatre"
                                label="Quatrième prénom"
                                disabled={
                                    !getValues('prenomUn') ||
                                    !getValues('prenomDeux') ||
                                    !getValues('prenomTrois')
                                }
                                {...register('prenomQuatre', {
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        'quatrième prénom',
                                    ),
                                })}
                                uppercase
                                state={formState.errors?.prenomQuatre ? 'error' : null}
                                message={formState.errors?.prenomQuatre?.message}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="given-name"
                                id="prenomUsuel"
                                label="Prénom usuel"
                                hintText="Prénom utilisé dans la vie courante, il doit être identique à un des prénoms renseignés dans le formulaire."
                                {...register('prenomUsuel', {
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_NOM_PRENOM,
                                        'prénom usuel',
                                    ),
                                    validate: (value) => validatePrenomUsuel(value),
                                })}
                                uppercase
                                state={formState.errors?.prenomUsuel ? 'error' : null}
                                message={formState.errors?.prenomUsuel?.message}
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputControl
                                autocomplete="bday"
                                id="dateNaissance"
                                label="Date de naissance *"
                                required
                                hintText="Exemple : 27/05/1975"
                                {...register('dateNaissance', {
                                    required: getMessageErreurChampManquant('Date de naissance'),
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
                                        '',
                                        MESSAGE_DATE_INVALIDE,
                                    ),
                                    validate: {
                                        validateDate: (value) => validateDate(value),
                                        validateAge: (value) => validateAge(value),
                                    },
                                    onChange: () => {
                                        setValueRecherche('');
                                        setValue('lieuNaissance', '');
                                    },
                                })}
                                state={formState.errors?.dateNaissance ? 'error' : null}
                                message={formState.errors?.dateNaissance?.message}
                                maskFonction={getDateFormatWithSlash}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <SelectPays
                                id="paysNaissance"
                                name="paysNaissance"
                                label="Pays de naissance *"
                                required
                                register={register('paysNaissance', {
                                    onChange: () => {
                                        setValueRecherche('');
                                        setValue('lieuNaissance', '');
                                    },
                                    required: getMessageErreurChampManquant('pays de Naissance'),
                                })}
                                placeholder="-- Choisir une option --"
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <SelectCommune
                                name="communneNaissance"
                                label="Lieu de naissance"
                                valueRecherche={valueRecherche}
                                setValueRecherche={setValueRecherche}
                            />
                        </Col>
                    </Row>
                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <SelectPays
                                id="nationalite"
                                name="nationalite"
                                label="Nationalité *"
                                required
                                register={register('nationalite', {
                                    required: getMessageErreurChampManquant('nationalité'),
                                })}
                                placeholder="-- Choisir une option --"
                            />
                        </Col>
                    </Row>
                    <Row gutters horizontalAlign="center" verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <div className="sia-input-alignementSansTexteAide">
                                <SelectControl
                                    id="typePieceIdentite"
                                    name="typePieceIdentite"
                                    label="Sélectionnez votre type de pièce d'identité *"
                                    options={formaterOptions(typePieceIdentite)}
                                    required
                                    {...register('typePieceIdentite', {
                                        required: getMessageErreurChampManquant(
                                            "type de pièce d'identité",
                                        ),
                                    })}
                                    placeholder="-- Choisir une option --"
                                />
                            </div>
                        </Col>
                        <Col col={12} lg={6}>
                            <InputControl
                                required
                                id="numeroPieceIdentite"
                                label="Numéro de la pièce d'identité *"
                                hintText="Le nombre de caractères de la pièce d'identité est limité à 50."
                                {...register('numeroPieceIdentite', {
                                    required: getMessageErreurChampManquant(
                                        "numéro de la pièce d'identité",
                                    ),
                                })}
                                state={formState.errors?.numeroPieceIdentite ? 'error' : null}
                                message={formState.errors?.numeroPieceIdentite?.message}
                                maxLength="50"
                            />
                        </Col>
                    </Row>
                    <Row gutters horizontalAlign="center" verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputControl
                                id="dateDelivrance"
                                label="Date de délivrance du document *"
                                required
                                hintText="Exemple : 01/02/1999"
                                {...register('dateDelivrance', {
                                    required: getMessageErreurChampManquant(
                                        'Date de délivrance du document',
                                    ),
                                    pattern: getPatternHookFormValidation(
                                        REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
                                        '',
                                        MESSAGE_DATE_INVALIDE,
                                    ),
                                    validate: {
                                        validateDate: (value) => validateDate(value),
                                    },
                                })}
                                state={formState.errors?.dateDelivrance ? 'error' : null}
                                message={formState.errors?.dateDelivrance?.message}
                                maskFonction={getDateFormatWithSlash}
                            />
                        </Col>
                        <Col col={12} lg={6}>
                            <InputFiles
                                name="piecesIdentite"
                                label="Ajouter ma pièce d'identité"
                                error={formState.errors ? formState.errors.piecesIdentite : null}
                                required
                                multiple
                            />
                        </Col>
                    </Row>

                    <Row className="margin-top-lg">
                        <Col col={12}>
                            <h3>Justificatifs de l'autorité parentale</h3>
                        </Col>
                    </Row>

                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputFiles
                                name="preuveAutoriteParentale"
                                label="Preuve de l'autorité parentale"
                                texteAide="Acte de naissance de l'enfant / Acte de reconnaissance / Acte de notoriété constatant la possession d'état ou une copie du livret de famille régulièrement tenu à jour ou une copie de la décision de justice confiant l'autorité parentale à l'un des deux parents ou à un tiers."
                                error={
                                    formState.errors
                                        ? formState.errors.preuveAutoriteParentale
                                        : null
                                }
                                required
                                multiple
                            />
                        </Col>
                    </Row>

                    <Row gutters verticalAlign="bottom">
                        <Col col={12} lg={6}>
                            <InputFiles
                                name="certificatHonneur"
                                label="Certificat sur l'honneur"
                                texteAide="Les documents acceptés sont :
- L'acte de naissance de l'enfant / l'acte de reconnaissance / l'acte de notoriété constatant la possession d'état
- Une copie du livret de famille régulièrement tenu à jour,
- Une copie de la décision de justice confiant l'autorité parentale à l'un des deux parents ou à un tiers.
Il est possible de téléverser plusieurs documents."
                                error={formState.errors ? formState.errors.certificatHonneur : null}
                                required
                                multiple
                            />
                        </Col>
                    </Row>

                    <Row className="margin-top-lg">
                        <Col>
                            <ButtonGroup inline align="right">
                                <Button
                                    name="etapePrecedente"
                                    variant="secondary"
                                    type="button"
                                    isLoading={
                                        isLoading ||
                                        isLoadingPreuveAutoriteParentale ||
                                        isLoadingCertificatHonneur
                                    }
                                    onClick={retourEtape}
                                >
                                    Retour
                                </Button>
                                <Button
                                    name="etapeSuivante"
                                    variant="primary"
                                    type="submit"
                                    isLoading={
                                        isLoading ||
                                        isLoadingPreuveAutoriteParentale ||
                                        isLoadingCertificatHonneur ||
                                        isLoadingPieceIdentite
                                    }
                                    disabled={!hasFormValid()}
                                >
                                    Étape suivante
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </SegmentLoader>
    );
};

CreerCompteResponsableLegal.propTypes = {
    dataResponsableLegalRenseignee: PropTypes.object,
    setEtapeResponsableLegal: PropTypes.func.isRequired,
    postApi: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    retourEtape: PropTypes.func.isRequired,
    erreur: PropTypes.bool,
    message: PropTypes.string,
    data: PropTypes.object,
    uuidBrouillon: PropTypes.string,
    isLoadingPreuveAutoriteParentale: PropTypes.bool,
    erreurPreuveAutoriteParentale: PropTypes.bool,
    dataRenseignerPreuveAutoriteParentale: PropTypes.object,
    isLoadingCertificatHonneur: PropTypes.bool,
    erreurCertificatHonneur: PropTypes.bool,
    dataRenseignerCertificatHonneur: PropTypes.object,
    isLoadingPieceIdentite: PropTypes.bool,
    erreurPieceIdentite: PropTypes.bool,
    dataRenseignerPieceIdentite: PropTypes.object,
    clearAlertes: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    const {
        isLoading: isLoadingResponsableLegal,
        erreur: erreurResponsableLegal,
        message,
        data: dataResponsableLegal,
    } = state.compte.creer.renseignerResponsableLegal || {};

    const {
        isLoading: isLoadingPreuveAutoriteParentale,
        erreur: erreurPreuveAutoriteParentale,
        data: dataRenseignerPreuveAutoriteParentale,
    } = state.compte.creer.renseignerPreuveAutoriteParentale || {};

    const {
        isLoading: isLoadingCertificatHonneur,
        erreur: erreurCertificatHonneur,
        data: dataRenseignerCertificatHonneur,
    } = state.compte.creer.renseignerCertificatHonneur || {};

    const {
        isLoading: isLoadingPieceIdentite,
        erreur: erreurPieceIdentite,
        data: dataRenseignerPieceIdentite,
    } = state.compte.creer.renseignerPieceIdentiteResponsableLegal || {};

    return {
        isLoading: isLoadingResponsableLegal,
        erreur: erreurResponsableLegal,
        message,
        data: dataResponsableLegal,
        isLoadingPreuveAutoriteParentale,
        erreurPreuveAutoriteParentale,
        dataRenseignerPreuveAutoriteParentale,
        isLoadingCertificatHonneur,
        erreurCertificatHonneur,
        dataRenseignerCertificatHonneur,
        isLoadingPieceIdentite,
        erreurPieceIdentite,
        dataRenseignerPieceIdentite,
    };
};

export default connect(mapStateToProps, {
    postApi: postAPI,
    reinitialiseApi: reinitialiseAPI,
    clearAlertes: hideAllNotification,
})(CreerCompteResponsableLegal);
